import { memo, useCallback } from 'react';
import { Typography, Button } from '@mui/material';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import { useTranslation } from 'react-i18next';
import { useToggle } from '@dfl/hook-utils';
import { IconButton } from '@dfl/mui-react-common';
import { PermissionCheck } from '@dfl/react-security';
import { IOrder } from '../../interfaces/IOrder';
import { ORDER_PERMISSIONS } from '../../constants/order-permissions';
import useOrderComplete from '../../hooks/useOrderComplete';
import OrderCompleteDialog from '../OrderCompleteDialog/OrderCompleteDialog';
import { ORDER_STATUS_TYPE_ENUM } from 'modules/sales/settings/order-status/constants';
import OrderConfirmComplete from './OrderConfirmComplete';
import { PAID_ORDER_ERRORS } from 'modules/sales/paid-order/constants';

type Props = {
  order: IOrder;
  variant?: 'button' | 'icon';
};

const OrderCompleteButton = ({ order, variant = 'button' }: Props) => {
  const { t } = useTranslation('order');
  const { isOpen, onClose, onOpen } = useToggle(false);
  const { isOpen: isOpenConfirm, onClose: onCloseConfirm, onOpen: onOpenConfirm } = useToggle(false);
  const { onSubmit, isLoading, control, error } = useOrderComplete(order?._id as string);

  const handleClose = useCallback(() => {
    onClose();
    onCloseConfirm();
  }, [onClose, onCloseConfirm]);

  return (
    <PermissionCheck permissions={ORDER_PERMISSIONS.ORDER_WRITE}>
      {variant === 'button' ? (
        <Button
          variant="contained"
          disabled={order?.status?.type === ORDER_STATUS_TYPE_ENUM.COMPLETED}
          startIcon={<DoneAllIcon />}
          onClick={onOpen}
        >
          <Typography noWrap>{t('header.actions.complete')}</Typography>
        </Button>
      ) : (
        <IconButton tooltip={t('header.actions.complete')} onClick={onOpen} disabled={order?.status?.type === ORDER_STATUS_TYPE_ENUM.COMPLETED} sx={{ p: 1 }}>
          <DoneAllIcon />
        </IconButton>
      )}

      <OrderCompleteDialog
        isOpen={isOpen}
        onClose={handleClose}
        order={order}
        onOpenConfirm={onOpenConfirm}
      />

      <OrderConfirmComplete
        open={isOpenConfirm}
        onClose={onCloseConfirm}
        control={control}
        onSubmit={onSubmit}
        isLoading={isLoading}
        error={error}
        errors={PAID_ORDER_ERRORS}
      />
    </PermissionCheck>
  );
};

export default memo(OrderCompleteButton);
